<template>
  <div id="virtual-benefit-fair-subview">
    <div class="mb-6 prose prose-md md:prose-lg">
      <h1>Virtual Benefit Fair</h1>
      <p>
        Browse our on-demand video library to learn about the benefit accounts
        administered by Rocky Mountain Reserve. Each video covers a specific
        plan type and is available to watch anytime at your convenience.
      </p>
    </div>

    <div class="video-grid">
      <button
        v-for="video in videos"
        :key="video.id"
        type="button"
        class="video-card"
        @click="openVideo(video)"
      >
        <div class="video-thumb">
          <img
            :src="`https://img.youtube.com/vi/${video.id}/mqdefault.jpg`"
            :alt="video.title"
            loading="lazy"
          />
          <div class="play-overlay" aria-hidden="true">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
              <circle cx="24" cy="24" r="24" fill="rgba(0,0,0,0.55)"/>
              <polygon points="19,14 37,24 19,34" fill="white"/>
            </svg>
          </div>
        </div>
        <div class="video-meta">
          <span class="video-title">{{ video.title }}</span>
        </div>
      </button>
    </div>

    <p class="mt-6 text-sm text-gray-600">
      Can't find what you need? <router-link to="/contact" class="font-semibold text-primary hover:underline">Contact us</router-link> and we'll be happy to help.
    </p>

    <!-- Modal -->
    <transition name="modal-fade">
      <div
        v-if="selectedVideo"
        class="modal-backdrop"
        @click.self="closeVideo"
        role="dialog"
        aria-modal="true"
        :aria-label="selectedVideo.title"
      >
        <div class="modal-box">
          <div class="modal-header">
            <span class="modal-title">{{ selectedVideo.title }}</span>
            <div class="modal-actions">
              <a
                :href="`https://www.youtube.com/watch?v=${selectedVideo.id}`"
                target="_blank"
                rel="noopener noreferrer"
                class="modal-yt-link"
              >Watch on YouTube ↗</a>
              <button type="button" class="modal-close" @click="closeVideo" aria-label="Close video">✕</button>
            </div>
          </div>
          <div class="modal-video">
            <iframe
              :src="embedSrc"
              :title="selectedVideo.title"
              allowfullscreen
              frameborder="0"
            ></iframe>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  data() {
    return {
      selectedVideo: null,
      videos: [
        { id: "Q0uxfT53KO8", title: "Health Flexible Spending Account (FSA)" },
        { id: "eikzOoZW3e8", title: "Health Savings Account (HSA)" },
        { id: "YcEZ-FW_vNk", title: "HSA & Limited-Purpose FSA" },
        { id: "1-vhvDnGJEY", title: "Health Reimbursement Arrangement (HRA)" },
        { id: "hpzAEBYkVtk", title: "Dependent Care FSA" },
        { id: "ijnO9YXPmdo", title: "Health FSA & Dependent Care FSA" },
        { id: "JI1pGdBzEBM", title: "Commuter: Parking & Transit" }
      ]
    };
  },
  computed: {
    embedSrc() {
      if (!this.selectedVideo) return "";
      return `https://www.youtube.com/embed/${this.selectedVideo.id}?autoplay=1&rel=0&modestbranding=1`;
    }
  },
  methods: {
    openVideo(video) {
      this.selectedVideo = video;
      document.addEventListener("keydown", this.onKeydown);
    },
    closeVideo() {
      this.selectedVideo = null;
      document.removeEventListener("keydown", this.onKeydown);
    },
    onKeydown(e) {
      if (e.key === "Escape") this.closeVideo();
    }
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onKeydown);
  }
};
</script>

<style scoped>
.video-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}
@media (min-width: 768px) {
  .video-grid { grid-template-columns: repeat(3, 1fr); }
}

.video-card {
  display: flex;
  flex-direction: column;
  border-radius: 0.625rem;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  background: #fff;
  text-align: left;
  font: inherit;
  cursor: pointer;
  padding: 0;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  transition: box-shadow 0.15s, transform 0.15s;
}
.video-card:hover {
  box-shadow: 0 4px 16px rgba(0,0,0,0.10);
  transform: translateY(-2px);
}
.video-card:focus-visible {
  outline: 2px solid #096843;
  outline-offset: 2px;
}

.video-thumb {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #000;
  overflow: hidden;
}
.video-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.play-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.15s;
}
.video-card:hover .play-overlay { opacity: 1; }

.video-meta {
  padding: 0.75rem;
  flex: 1;
}
.video-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  line-height: 1.4;
}

/* Modal */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
}
.modal-box {
  background: #111827;
  border-radius: 0.875rem;
  overflow: hidden;
  width: 100%;
  max-width: 52rem;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.5);
}
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.875rem 1.25rem;
}
.modal-title {
  font-size: 0.9375rem;
  font-weight: 700;
  color: #ffffff;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.modal-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
}
.modal-yt-link {
  font-size: 0.8125rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.65);
  text-decoration: none;
  white-space: nowrap;
  transition: color 0.15s;
}
.modal-yt-link:hover { color: #ffffff; }
.modal-close {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.65);
  font-size: 1rem;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  line-height: 1;
  transition: color 0.15s;
}
.modal-close:hover { color: #ffffff; }
.modal-video {
  position: relative;
  aspect-ratio: 16 / 9;
  width: 100%;
}
.modal-video iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  border: 0;
}

.modal-fade-enter-active,
.modal-fade-leave-active { transition: opacity 0.2s ease; }
.modal-fade-enter,
.modal-fade-leave-to { opacity: 0; }
</style>
